import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Bold, Italic, Underline, Type, Palette, AlignLeft, AlignCenter, AlignRight, List, ListOrdered, Link } from 'lucide-react';

interface EmailEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  height?: number;
  onFocus?: () => void;
}

export interface EmailEditorHandle {
  insertText: (text: string) => void;
  focus: () => void;
  saveSelection: () => void;
}

const COLORS = [
  '#000000', '#434343', '#666666', '#999999', '#B7B7B7', '#CCCCCC', '#D9D9D9', '#EFEFEF',
  '#F3F3F3', '#FFFFFF', '#980000', '#FF0000', '#FF9900', '#FFFF00', '#00FF00', '#00FFFF',
  '#4A86E8', '#0000FF', '#9900FF', '#FF00FF', '#E6B8AF', '#F4CCCC', '#FCE5CD', '#FFF2CC',
  '#D9EAD3', '#D0E0E3', '#C9DAF8', '#CFE2F3', '#D9D2E9', '#EAD1DC'
];

export const EmailEditor = forwardRef<EmailEditorHandle, EmailEditorProps>(function EmailEditor(
  { content, onChange, placeholder = 'Start typing...', height = 400, onFocus },
  ref
) {
  const editorRef = useRef<HTMLDivElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showFontSize, setShowFontSize] = useState(false);

  const saveSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editorRef.current) return;

    const range = selection.getRangeAt(0);
    if (editorRef.current.contains(range.commonAncestorContainer)) {
      savedRangeRef.current = range.cloneRange();
    }
  };

  const restoreSelection = () => {
    if (!savedRangeRef.current) return false;

    const selection = window.getSelection();
    if (!selection) return false;

    selection.removeAllRanges();
    selection.addRange(savedRangeRef.current);
    return true;
  };

  const syncContent = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  useImperativeHandle(ref, () => ({
    insertText(text: string) {
      editorRef.current?.focus();
      restoreSelection();

      const inserted = document.execCommand('insertText', false, text);
      if (!inserted && editorRef.current) {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.deleteContents();
          range.insertNode(document.createTextNode(text));
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        } else {
          editorRef.current.innerHTML += text;
        }
      }

      syncContent();
      saveSelection();
    },
    focus() {
      editorRef.current?.focus();
    },
    saveSelection,
  }));

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== content) {
      editorRef.current.innerHTML = content;
    }
  }, [content]);

  useEffect(() => {
    const handleSelectionChange = () => saveSelection();
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  const handleInput = () => {
    syncContent();
    saveSelection();
  };

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    syncContent();
    saveSelection();
  };

  const applyColor = (color: string) => {
    execCommand('foreColor', color);
    setShowColorPicker(false);
  };

  const applyFontSize = (size: string) => {
    execCommand('fontSize', size);
    setShowFontSize(false);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    syncContent();
    saveSelection();
  };

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white flex flex-col h-full">
      <div className="border-b border-slate-200 bg-slate-50 p-2 flex flex-wrap gap-1 shrink-0">
        <button
          type="button"
          onClick={() => execCommand('bold')}
          className="p-2 hover:bg-slate-200 rounded transition-colors"
          title="Bold"
        >
          <Bold size={18} />
        </button>
        <button
          type="button"
          onClick={() => execCommand('italic')}
          className="p-2 hover:bg-slate-200 rounded transition-colors"
          title="Italic"
        >
          <Italic size={18} />
        </button>
        <button
          type="button"
          onClick={() => execCommand('underline')}
          className="p-2 hover:bg-slate-200 rounded transition-colors"
          title="Underline"
        >
          <Underline size={18} />
        </button>

        <div className="w-px bg-slate-300 mx-1"></div>

        <div className="relative">
          <button
            type="button"
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="p-2 hover:bg-slate-200 rounded transition-colors"
            title="Text Color"
          >
            <Palette size={18} />
          </button>
          {showColorPicker && (
            <div className="absolute top-full left-0 mt-1 p-2 bg-white border border-slate-200 rounded-lg shadow-lg z-50">
              <div className="grid grid-cols-10 gap-1 w-64">
                {COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => applyColor(color)}
                    className="w-6 h-6 rounded border border-slate-300 hover:scale-110 transition-transform"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => setShowFontSize(!showFontSize)}
            className="p-2 hover:bg-slate-200 rounded transition-colors"
            title="Font Size"
          >
            <Type size={18} />
          </button>
          {showFontSize && (
            <div className="absolute top-full left-0 mt-1 p-2 bg-white border border-slate-200 rounded-lg shadow-lg z-50 min-w-[120px]">
              <button
                type="button"
                onClick={() => applyFontSize('1')}
                className="block w-full text-left px-3 py-2 hover:bg-slate-100 rounded text-xs"
              >
                Small
              </button>
              <button
                type="button"
                onClick={() => applyFontSize('3')}
                className="block w-full text-left px-3 py-2 hover:bg-slate-100 rounded text-sm"
              >
                Normal
              </button>
              <button
                type="button"
                onClick={() => applyFontSize('5')}
                className="block w-full text-left px-3 py-2 hover:bg-slate-100 rounded text-lg"
              >
                Large
              </button>
              <button
                type="button"
                onClick={() => applyFontSize('7')}
                className="block w-full text-left px-3 py-2 hover:bg-slate-100 rounded text-2xl"
              >
                Huge
              </button>
            </div>
          )}
        </div>

        <div className="w-px bg-slate-300 mx-1"></div>

        <button
          type="button"
          onClick={() => execCommand('justifyLeft')}
          className="p-2 hover:bg-slate-200 rounded transition-colors"
          title="Align Left"
        >
          <AlignLeft size={18} />
        </button>
        <button
          type="button"
          onClick={() => execCommand('justifyCenter')}
          className="p-2 hover:bg-slate-200 rounded transition-colors"
          title="Align Center"
        >
          <AlignCenter size={18} />
        </button>
        <button
          type="button"
          onClick={() => execCommand('justifyRight')}
          className="p-2 hover:bg-slate-200 rounded transition-colors"
          title="Align Right"
        >
          <AlignRight size={18} />
        </button>

        <div className="w-px bg-slate-300 mx-1"></div>

        <button
          type="button"
          onClick={() => execCommand('insertUnorderedList')}
          className="p-2 hover:bg-slate-200 rounded transition-colors"
          title="Bullet List"
        >
          <List size={18} />
        </button>
        <button
          type="button"
          onClick={() => execCommand('insertOrderedList')}
          className="p-2 hover:bg-slate-200 rounded transition-colors"
          title="Numbered List"
        >
          <ListOrdered size={18} />
        </button>

        <div className="w-px bg-slate-300 mx-1"></div>

        <button
          type="button"
          onClick={() => {
            const url = prompt('Enter URL:');
            if (url) execCommand('createLink', url);
          }}
          className="p-2 hover:bg-slate-200 rounded transition-colors"
          title="Insert Link"
        >
          <Link size={18} />
        </button>
      </div>

      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        onBlur={saveSelection}
        onFocus={onFocus}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        className="p-4 outline-none flex-1 overflow-y-auto prose max-w-none"
        style={{ minHeight: height - 50 }}
        data-placeholder={placeholder}
      />

      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #94a3b8;
          pointer-events: none;
          position: absolute;
        }
        [contenteditable] {
          font-family: Arial, sans-serif;
          font-size: 16px;
          line-height: 1.6;
        }
      `}</style>
    </div>
  );
});
